import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import {
  Box, Card, Stack, MenuItem, Button, LinearProgress,
  Typography, Alert, Snackbar, Paper, FormControl,
  InputLabel, Select, Fade
} from "@mui/material";
import {
  FileDownload as DownloadIcon,
  Refresh as RefreshIcon,
  AccountBalance as AccountBalanceIcon
} from "@mui/icons-material";
import AppHeader from "../../components/AppHeader";

const AccountExport = ({ setIsLoggedIn }) => {
  const [loading, setLoading] = useState(false);
  const [runs, setRuns] = useState([]);
  const [selectedRunId, setSelectedRunId] = useState("");
  const [format, setFormat] = useState("GENERIC_CSV");
  const [statusMsg, setStatusMsg] = useState({ open: false, msg: "", severity: "success" });

  const tenant = localStorage.getItem("tenant") || "";

  const fetchRuns = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${process.env.REACT_APP_API_URL1}/sm/payroll/runs`, {
        headers: { Authorization: `Bearer ${token}`, "X-Tenant": tenant },
        withCredentials: true
      });
      if (response.data.success) {
        // Sort runs by run date descending
        const sortedRuns = (response.data.data || []).filter(run => ['FINALIZED', 'PAID'].includes(run.status)).sort(
          (a, b) => new Date(b.run_date) - new Date(a.run_date)
        );
        setRuns(sortedRuns);
        
        // Auto-select the latest finalized run if available
        const latestRun = sortedRuns[0];
        if (latestRun) {
          setSelectedRunId(latestRun.id);
        }
      }
    } catch (error) {
      console.error("Error fetching payroll runs:", error);
      setStatusMsg({ open: true, msg: "Error fetching payroll runs", severity: "error" });
    } finally {
      setLoading(false);
    }
  }, [tenant]);

  useEffect(() => {
    fetchRuns();
  }, [fetchRuns]);

  const handleExport = async () => {
    if (!selectedRunId) {
      setStatusMsg({ open: true, msg: "Please select a payroll run to export", severity: "warning" });
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL1}/sm/payroll/accounting-export?run_id=${selectedRunId}&format=${format}`,
        {
          headers: { Authorization: `Bearer ${token}`, "X-Tenant": tenant },
          withCredentials: true,
          responseType: "blob"
        }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `Journal_Export_Run_${selectedRunId}.csv`);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);

      setStatusMsg({ open: true, msg: "Journal exported successfully!", severity: "success" });
    } catch (error) {
      console.error("Error exporting journal:", error);
      setStatusMsg({ open: true, msg: "Failed to export journal. Check that accounting mapping is complete.", severity: "error" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", minHeight: "100vh", bgcolor: "#f8fafc" }}>
      <AppHeader title="Account Export" setIsLoggedIn={setIsLoggedIn} />

      <Box sx={{ p: 4, flexGrow: 1, maxWidth: 800, mx: "auto", width: "100%" }}>
        <Fade in timeout={500}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 4 }}>
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 800, color: "#0f172a", letterSpacing: "-0.02em", mb: 1 }}>
                Account Export
              </Typography>
              <Typography variant="body1" sx={{ color: "#64748b" }}>
                Generate and download general ledger journal files for completed payroll runs.
              </Typography>
            </Box>

            <Card sx={{ p: 4, borderRadius: 4, boxShadow: "0 4px 20px 0 rgba(0,0,0,0.04)", bgcolor: "#fff" }}>
              {loading && <LinearProgress sx={{ borderRadius: 1, mb: 3, bgcolor: "#f1f5f9", "& .MuiLinearProgress-bar": { bgcolor: "primary.main" } }} />}
              
              <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 4 }}>
                <Box sx={{ p: 1.5, bgcolor: (theme) => `${theme.palette.primary.main}33`, borderRadius: 2 }}>
                  <AccountBalanceIcon sx={{ color: "primary.main" }} />
                </Box>
                <Box>
                  <Typography variant="h6" fontWeight="bold" sx={{ color: "#1e293b" }}>
                    Configure Journal Export
                  </Typography>
                  <Typography variant="caption" sx={{ color: "#64748b" }}>
                    Select a cycle to export. Ensure your Cost Centre mappings are up-to-date.
                  </Typography>
                </Box>
              </Stack>

              <Stack spacing={3}>
                <FormControl fullWidth size="small">
                  <InputLabel id="payroll-run-select-label">Payroll Run</InputLabel>
                  <Select
                    labelId="payroll-run-select-label"
                    value={selectedRunId}
                    label="Payroll Run"
                    onChange={(e) => setSelectedRunId(e.target.value)}
                    sx={{ borderRadius: 2 }}
                  >
                    {runs.map((run) => (
                      <MenuItem key={run.id} value={run.id}>
                        Run #{run.id} — Month {run.period_month}/{run.period_year} ({run.status}) — {parseFloat(run.total_net_pay || 0).toLocaleString()} MMK ({run.staff_count} staff)
                      </MenuItem>
                    ))}
                    {runs.length === 0 && (
                      <MenuItem value="" disabled>
                        No payroll runs found
                      </MenuItem>
                    )}
                  </Select>
                </FormControl>

                <FormControl fullWidth size="small">
                  <InputLabel id="export-format-label">Export Format</InputLabel>
                  <Select
                    labelId="export-format-label"
                    value={format}
                    label="Export Format"
                    onChange={(e) => setFormat(e.target.value)}
                    sx={{ borderRadius: 2 }}
                  >
                    <MenuItem value="GENERIC_CSV">Generic CSV (Standard Accounting Journal)</MenuItem>
                    <MenuItem value="SAP_CSV" disabled>SAP Journal Integration (Coming Soon)</MenuItem>
                    <MenuItem value="ODOO_CSV" disabled>Odoo Journal Integration (Coming Soon)</MenuItem>
                  </Select>
                </FormControl>

                <Stack direction="row" spacing={2} justifyContent="flex-end" sx={{ pt: 2 }}>
                  <Button
                    variant="outlined"
                    startIcon={<RefreshIcon />}
                    onClick={fetchRuns}
                    sx={{
                      borderRadius: 2,
                      textTransform: "none",
                      fontWeight: 600,
                      borderColor: "#cbd5e1",
                      color: "#475569",
                      "&:hover": { bgcolor: "#f8fafc", borderColor: "#cbd5e1" }
                    }}
                  >
                    Refresh
                  </Button>
                  <Button
                    variant="contained"
                    startIcon={<DownloadIcon />}
                    onClick={handleExport}
                    disabled={loading || !selectedRunId}
                    sx={{
                      borderRadius: 2,
                      textTransform: "none",
                      fontWeight: 700,
                      bgcolor: "primary.main",
                      boxShadow: (theme) => `0 4px 12px ${theme.palette.primary.main}33`,
                      "&:hover": { bgcolor: "primary.dark" }
                    }}
                  >
                    Export Journal
                  </Button>
                </Stack>
              </Stack>
            </Card>

            <Paper sx={{ p: 3, borderRadius: 3, border: "1px solid #e2e8f0", bgcolor: "#f8fafc" }} elevation={0}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "#334155", mb: 1 }}>
                💡 Quick Tip:
              </Typography>
              <Typography variant="body2" sx={{ color: "#64748b", lineHeight: 1.6 }}>
                The exported journal includes double-entry entries split by Employee Cost Centre Allocations. Pay Elements that do not have an active GL account code mapping will be categorized under <strong>"UNMAPPED"</strong>. Please visit the <strong>Accounting Map</strong> screen to map any missing codes.
              </Typography>
            </Paper>
          </Box>
        </Fade>
      </Box>

      <Snackbar
        open={statusMsg.open}
        autoHideDuration={6000}
        onClose={() => setStatusMsg({ ...statusMsg, open: false })}
      >
        <Alert
          severity={statusMsg.severity}
          variant="filled"
          sx={{ width: "100%", borderRadius: 3, fontWeight: 600, boxShadow: "0 10px 15px -3px rgba(0,0,0,0.1)" }}
          onClose={() => setStatusMsg({ ...statusMsg, open: false })}
        >
          {statusMsg.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AccountExport;
